import { BaseService } from './base';

// ============================================
// Types
// ============================================

export type FeedStatus = 'pending' | 'fetching' | 'ready' | 'error' | 'stale';
export type ExportStatus = 'queued' | 'processing' | 'completed' | 'failed';
export type ExportFormat = 'xml' | 'csv' | 'tsv';
export type FeedType = 'google_shopping' | 'meta' | 'tiktok' | 'amazon';
export type MappingMode = 'use_field' | 'static_value' | 'combine' | 'extract_from' | 'lookup_table' | 'leave_empty';
export type FilterOperator =
  | 'is_not_empty' | 'is_empty' | 'equals' | 'not_equals'
  | 'contains' | 'not_contains' | 'starts_with' | 'ends_with'
  | 'is_greater_than' | 'is_less_than';

export type ShopPlatform = 'shopify' | 'woocommerce' | 'magento' | 'bigcommerce' | 'custom' | 'other';

// ── Shop ────────────────────────────────────

export interface Shop {
  id: string;
  tenant_id: string;
  name: string;
  platform: ShopPlatform;
  shop_url: string | null;
  logo_url: string | null;
  metadata: Record<string, unknown>;
  is_active: boolean;
  feed_count: number;
  created_at: string;
  updated_at: string;
}

// ── Feed ────────────────────────────────────

export interface Feed {
  id: string;
  shop_id: string;
  name: string;
  source_url: string | null;
  source_type: string;
  status: FeedStatus;
  error_message: string | null;
  total_products: number;
  detected_fields: string[];
  last_fetched_at: string | null;
  feed_type: FeedType;
  public_token: string;
  created_at: string;
  updated_at: string;
}

export interface FeedProduct {
  id: string;
  feed_id: string;
  sort_index: number;
  raw_data: Record<string, unknown>;
  created_at: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

// ── Channel ─────────────────────────────────

export interface ChannelField {
  name: string;
  required: boolean;
}

export interface ChannelSpec {
  id: string;
  label: string;
  supported_formats: ExportFormat[];
  fields: ChannelField[];
}

// ── Mapping ─────────────────────────────────

export interface CombineToken {
  id?: string;
  type: 'field' | 'text';
  token_type?: 'field' | 'text';
  value: string;
  sort_order?: number;
}

export interface LookupEntry {
  id?: string;
  match_value: string;
  output_value: string;
  is_default?: boolean;
  sort_order?: number;
}

export interface Condition {
  id?: string;
  field: string;
  operator: string;
  value?: string | null;
  logic: 'AND' | 'OR';
  sort_order?: number;
}

export interface MappingRule {
  id?: string;
  gs_field: string;
  mode: MappingMode;
  is_required?: boolean;
  source_field?: string | null;
  static_value?: string | null;
  tokens?: CombineToken[];
  separator?: string;
  regex_pattern?: string | null;
  default_value?: string | null;
  lookup_entries?: LookupEntry[];
  apply_to?: 'all' | 'conditional';
  conditions?: Condition[];
}

export interface MappingConfig {
  id: string;
  feed_id: string;
  name: string;
  is_active: boolean;
  rules: MappingRule[];
  created_at: string;
  updated_at: string;
}

export interface MappingConfigInput {
  name?: string;
  rules: MappingRule[];
}

export interface MappingSuggestion {
  gs_field: string;
  suggested_source_field: string;
  confidence: number;
}

export interface MappingValidation {
  valid: boolean;
  errors: { field: string; message: string }[];
  warnings: { field: string; message: string }[];
}

export interface MappingPreviewItem {
  original: Record<string, unknown>;
  mapped: Record<string, string>;
}

// ── Filter ──────────────────────────────────

export interface FilterRule {
  id?: string;
  field: string;
  operator: FilterOperator;
  value?: string | null;
  logic: 'AND' | 'OR';
}

export interface FilterConfig {
  include: FilterRule[];
  exclude: FilterRule[];
}

export interface FilterPreview {
  total_products: number;
  included_count: number;
  excluded_count: number;
}

// ── Canvas ──────────────────────────────────

export type CanvasElementType = 'text' | 'image' | 'rect' | 'circle';

export interface BaseCanvasElement {
  type: CanvasElementType;
  id?: string;
  name?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  locked?: boolean;
  rotation?: number;
  visible?: boolean;
  opacity?: number;
  shadow?: { x: number; y: number; blur: number; color: string };
}

export interface TextElement extends BaseCanvasElement {
  type: 'text';
  content: string;
  fontSize: number;
  fontWeight: string;
  fontFamily: string;
  color: string;
  textAlign: 'left' | 'center' | 'right';
  maxWidth?: number;
  maxChars?: number;
  renderHtml?: boolean;
}

export interface ImageElement extends BaseCanvasElement {
  type: 'image';
  src: string;
  objectFit: 'cover' | 'contain' | 'fill';
  borderRadius: number;
  removeBg?: boolean;
  bgRemovedSrc?: string;
}

export interface RectElement extends BaseCanvasElement {
  type: 'rect';
  fill: string;
  borderRadius: number;
  border: string;
}

export interface CircleElement extends BaseCanvasElement {
  type: 'circle';
  fill: string;
}

export type CanvasElement = TextElement | ImageElement | RectElement | CircleElement;

export interface CanvasDesign {
  id: string;
  feed_id: string;
  name: string;
  template_id: string | null;
  width: number;
  height: number;
  elements: CanvasElement[];
  created_at: string;
  updated_at: string;
}

export interface CanvasRender {
  id: string;
  design_id: string;
  product_idx: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  cloudinary_url: string | null;
  file_size_bytes: number | null;
  error_message: string | null;
}

export interface RenderTriggerResult {
  status: string;
  total_products: number;
}

export interface RenderProgress {
  status: 'idle' | 'rendering' | 'completed' | 'failed';
  total: number;
  completed: number;
  failed: number;
  queued: number;
  processing: number;
}

// ── Schedule ────────────────────────────────

export type ScheduleFrequency = 'hourly' | 'daily' | 'weekly' | 'custom_cron';

export interface Schedule {
  id: string;
  feed_id: string;
  frequency: ScheduleFrequency;
  cron_expr: string | null;
  is_active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  auto_export: boolean;
  created_at: string;
  updated_at: string;
}

// ── Export ───────────────────────────────────

export interface FeedExport {
  id: string;
  feed_id: string;
  format: ExportFormat;
  status: ExportStatus;
  download_url?: string;
  public_url?: string;
  file_size_bytes: number | null;
  product_count: number | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

// ============================================
// Feed type → allowed export formats
// ============================================

export const FEED_TYPE_FORMATS: Record<FeedType, ExportFormat[]> = {
  google_shopping: ['xml', 'csv'],
  meta: ['xml', 'csv'],
  tiktok: ['csv'],
  amazon: ['tsv'],
};

// ============================================
// Service
// ============================================

class FeedWatchService extends BaseService {
  private readonly base = '/feedwatch';

  // ── Upload ──────────────────────────────────

  async uploadCanvasAsset(file: File): Promise<{ url: string; public_id: string; width: number; height: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const authHeader = this.getAuthHeader();
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001');
    const res = await fetch(`${apiBase}/api${this.base}/upload`, {
      method: 'POST',
      headers: {
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Upload failed');
    }
    const body = await res.json();
    return body.data;
  }

  // ── Shops ───────────────────────────────────

  async createShop(data: {
    name: string;
    platform?: ShopPlatform;
    shop_url?: string;
  }): Promise<Shop> {
    return this.makeRequest<Shop>(`${this.base}/shops`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listShops(page = 1, limit = 20): Promise<PaginatedResponse<Shop>> {
    return this.makeRequest<PaginatedResponse<Shop>>(
      `${this.base}/shops?page=${page}&limit=${limit}`
    );
  }

  async getShop(shopId: string): Promise<Shop> {
    return this.makeRequest<Shop>(`${this.base}/shops/${shopId}`);
  }

  async updateShop(
    shopId: string,
    data: Partial<Pick<Shop, 'name' | 'platform' | 'shop_url'>>
  ): Promise<Shop> {
    return this.makeRequest<Shop>(`${this.base}/shops/${shopId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteShop(shopId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/shops/${shopId}`, {
      method: 'DELETE',
    });
  }

  async listShopFeeds(
    shopId: string,
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<Feed>> {
    return this.makeRequest<PaginatedResponse<Feed>>(
      `${this.base}/shops/${shopId}/feeds?page=${page}&limit=${limit}`
    );
  }

  // ── Feeds ───────────────────────────────────

  async createFeed(data: {
    name: string;
    shop_id?: string;
    source_url?: string;
    source_type?: 'url' | 'paste';
    raw_xml?: string;
    feed_type?: FeedType;
  }): Promise<Feed> {
    return this.makeRequest<Feed>(`${this.base}/feeds`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listFeeds(page = 1, limit = 20, feedType?: FeedType): Promise<PaginatedResponse<Feed>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (feedType) params.set('feed_type', feedType);
    return this.makeRequest<PaginatedResponse<Feed>>(
      `${this.base}/feeds?${params}`
    );
  }

  async getFeed(feedId: string): Promise<Feed> {
    return this.makeRequest<Feed>(`${this.base}/feeds/${feedId}`);
  }

  async refreshFeed(feedId: string): Promise<Feed> {
    return this.makeRequest<Feed>(`${this.base}/feeds/${feedId}/refresh`, {
      method: 'POST',
    });
  }

  async deleteFeed(feedId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/feeds/${feedId}`, {
      method: 'DELETE',
    });
  }

  async listProducts(
    feedId: string,
    page = 1,
    limit = 50,
    search?: string
  ): Promise<PaginatedResponse<FeedProduct>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return this.makeRequest<PaginatedResponse<FeedProduct>>(
      `${this.base}/feeds/${feedId}/products?${params}`
    );
  }

  async getProduct(feedId: string, sortIndex: number): Promise<FeedProduct> {
    return this.makeRequest<FeedProduct>(
      `${this.base}/feeds/${feedId}/products/${sortIndex}`
    );
  }

  // ── Channels ────────────────────────────────

  async getChannelSpec(feedType: FeedType): Promise<ChannelSpec> {
    return this.makeRequest<ChannelSpec>(`${this.base}/channels/${feedType}`);
  }

  async listChannels(): Promise<ChannelSpec[]> {
    return this.makeRequest<ChannelSpec[]>(`${this.base}/channels`);
  }

  // ── Mappings ────────────────────────────────

  async getMappings(feedId: string): Promise<MappingConfig | null> {
    return this.makeRequest<MappingConfig | null>(
      `${this.base}/feeds/${feedId}/mappings`
    );
  }

  async updateMappings(feedId: string, config: MappingConfigInput): Promise<MappingConfig> {
    return this.makeRequest<MappingConfig>(
      `${this.base}/feeds/${feedId}/mappings`,
      {
        method: 'PUT',
        body: JSON.stringify(config),
      }
    );
  }

  async validateMappings(feedId: string): Promise<MappingValidation> {
    return this.makeRequest<MappingValidation>(
      `${this.base}/feeds/${feedId}/mappings/validate`,
      { method: 'POST' }
    );
  }

  async previewMappings(feedId: string, count = 5): Promise<MappingPreviewItem[]> {
    return this.makeRequest<MappingPreviewItem[]>(
      `${this.base}/feeds/${feedId}/mappings/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ count }),
      }
    );
  }

  async suggestMappings(feedId: string): Promise<MappingSuggestion[]> {
    return this.makeRequest<MappingSuggestion[]>(
      `${this.base}/feeds/${feedId}/mappings/suggest`,
      { method: 'POST' }
    );
  }

  // ── Filters ─────────────────────────────────

  async getFilters(feedId: string): Promise<FilterConfig> {
    return this.makeRequest<FilterConfig>(
      `${this.base}/feeds/${feedId}/filters`
    );
  }

  async updateFilters(feedId: string, config: FilterConfig): Promise<FilterConfig> {
    return this.makeRequest<FilterConfig>(
      `${this.base}/feeds/${feedId}/filters`,
      {
        method: 'PUT',
        body: JSON.stringify(config),
      }
    );
  }

  async previewFilters(feedId: string): Promise<FilterPreview> {
    return this.makeRequest<FilterPreview>(
      `${this.base}/feeds/${feedId}/filters/preview`,
      { method: 'POST' }
    );
  }

  // ── Exports ─────────────────────────────────

  async createExport(
    feedId: string,
    format: ExportFormat,
    designId?: string
  ): Promise<FeedExport> {
    const body: Record<string, string> = { format };
    if (designId) body.design_id = designId;
    return this.makeRequest<FeedExport>(
      `${this.base}/feeds/${feedId}/exports`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  }

  async listExports(feedId: string): Promise<FeedExport[]> {
    return this.makeRequest<FeedExport[]>(
      `${this.base}/feeds/${feedId}/exports`
    );
  }

  async getExport(feedId: string, exportId: string): Promise<FeedExport> {
    return this.makeRequest<FeedExport>(
      `${this.base}/feeds/${feedId}/exports/${exportId}`
    );
  }

  // ── Canvas ─────────────────────────────────

  async createDesign(
    feedId: string,
    data: { name: string; template_id: string; width: number; height: number; elements?: CanvasElement[] }
  ): Promise<CanvasDesign> {
    return this.makeRequest<CanvasDesign>(
      `${this.base}/feeds/${feedId}/canvas/designs`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async listDesigns(feedId: string): Promise<CanvasDesign[]> {
    return this.makeRequest<CanvasDesign[]>(
      `${this.base}/feeds/${feedId}/canvas/designs`
    );
  }

  async getDesign(feedId: string, designId: string): Promise<CanvasDesign> {
    return this.makeRequest<CanvasDesign>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}`
    );
  }

  async updateDesign(
    feedId: string,
    designId: string,
    data: Partial<Pick<CanvasDesign, 'name' | 'width' | 'height' | 'elements'>>
  ): Promise<CanvasDesign> {
    return this.makeRequest<CanvasDesign>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
  }

  async deleteDesign(feedId: string, designId: string): Promise<void> {
    await this.makeRequest<void>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}`,
      { method: 'DELETE' }
    );
  }

  async exportDesign(
    feedId: string,
    designId: string,
    options: { format?: 'png' | 'jpeg'; quality?: number; scale?: number; sortIndex?: number } = {},
  ): Promise<Blob> {
    const token = this.getAuthHeader();
    const orgId = localStorage.getItem('current_organization_id');
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

    const resp = await fetch(`${apiBase}${this.base}/feeds/${feedId}/canvas/designs/${designId}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: token } : {}),
        ...(orgId ? { 'x-organization-id': orgId } : {}),
      },
      body: JSON.stringify({
        format: options.format ?? 'png',
        quality: options.quality ?? 100,
        scale: options.scale ?? 2,
        ...(options.sortIndex !== undefined ? { sortIndex: options.sortIndex } : {}),
      }),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Export failed: ${text}`);
    }

    return resp.blob();
  }

  async triggerRender(
    feedId: string,
    designId: string,
    options?: { productIndices?: number[] }
  ): Promise<RenderTriggerResult> {
    const body = options?.productIndices?.length
      ? JSON.stringify({ product_indices: options.productIndices })
      : undefined;
    return this.makeRequest<RenderTriggerResult>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}/render`,
      { method: 'POST', body }
    );
  }

  async cancelRender(feedId: string, designId: string): Promise<void> {
    await this.makeRequest<void>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}/render/cancel`,
      { method: 'POST' }
    );
  }

  async getRenderProgress(feedId: string, designId: string): Promise<RenderProgress> {
    return this.makeRequest<RenderProgress>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}/render/progress`
    );
  }

  async listRenders(feedId: string, designId: string): Promise<CanvasRender[]> {
    return this.makeRequest<CanvasRender[]>(
      `${this.base}/feeds/${feedId}/canvas/designs/${designId}/renders`
    );
  }

  // ── Schedules ──────────────────────────────

  async createSchedule(
    feedId: string,
    data: { frequency: ScheduleFrequency; cron_expr?: string; auto_export?: boolean }
  ): Promise<Schedule> {
    return this.makeRequest<Schedule>(
      `${this.base}/feeds/${feedId}/schedules`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async listSchedules(feedId: string): Promise<Schedule[]> {
    return this.makeRequest<Schedule[]>(
      `${this.base}/feeds/${feedId}/schedules`
    );
  }

  async updateSchedule(
    feedId: string,
    scheduleId: string,
    data: Partial<Pick<Schedule, 'frequency' | 'cron_expr' | 'is_active' | 'auto_export'>>
  ): Promise<Schedule> {
    return this.makeRequest<Schedule>(
      `${this.base}/feeds/${feedId}/schedules/${scheduleId}`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
  }

  async deleteSchedule(feedId: string, scheduleId: string): Promise<void> {
    await this.makeRequest<void>(
      `${this.base}/feeds/${feedId}/schedules/${scheduleId}`,
      { method: 'DELETE' }
    );
  }
}

export const feedWatchService = new FeedWatchService();
